
import Vue from 'vue';
import { UserModel, CourseModel } from '@descomplica/dex-models';
import Post from '@/components/forum/Post.vue';
import { ChannelModel } from '@/models/forumAPI/ChannelModel';
import CommentCard from '@/components/comment/CommentCard.vue';
import CommentInput from '@/components/comment/CommentInput.vue';
import ContentAndSolutions from '@/components/ContentAndSolutions.vue';
import { DiscussionModel } from '@/models/forumAPI/DiscussionModel';
import BackButton from '@/components/BackButton.vue';
import { UnifiedCommentModel } from '@/models/UnifiedCommentModel';
import { FileModel } from '../models/FileModel';
import scrollDirective from '@/directives/scroll';
import Analytics from '@/helpers/analytics';
import { EVENT_LABELS } from '@/helpers/analytics/labels';
import { getCourseCategory } from '@/helpers/category';

export default Vue.extend({
  components: {
    BackButton,
    CommentCard,
    CommentInput,
    ContentAndSolutions,
    Post,
  },
  directives: {
    scroll: scrollDirective,
  },
  data() {
    return {
      isSending: false as Boolean,
      isDiscussionName: false as Boolean,
    };
  },
  computed: {
    user(): UserModel {
      return this.$store.getters.currentUser;
    },
    course(): CourseModel {
      return this.user.getCourse();
    },
    channelId(): string {
      return this.$route.params.channelId;
    },
    channel(): ChannelModel {
      return this.$store.getters.forumAPIChannelById(this.channelId);
    },
    discussion(): DiscussionModel {
      const { discussionId } = this.$route.params;
      return this.$store.getters.forumAPIDiscussionById(discussionId);
    },
    comments(): UnifiedCommentModel[] {
      return !this.discussion ? [] : this.$store.getters.commentsByDiscussionId(this.discussion.id);
    },
    post(): any {
      return this.$refs.post;
    },
  },
  watch: {
    comments: {
      immediate: true,
      handler(current: UnifiedCommentModel[] = [], previous: UnifiedCommentModel[] = []) {
        // Get contentReactionsIds for all previous answers and its replies.
        const previousAnswersIds = previous.flatMap((i) =>
          i.replies.map((r) => r.contentReactionId),
        );
        const previousIds = previous
          .map((item) => item.contentReactionId)
          .concat(previousAnswersIds);

        // Get contentReactionsIds for all current answers and its replies.
        const currentAnswersIds = current.flatMap((i) => i.replies.map((r) => r.contentReactionId));
        const currentIds = current.map((item) => item.contentReactionId).concat(currentAnswersIds);

        // Get only the contentReactionsIds that was not loaded yet.
        const pendingContentsIds = currentIds.filter(
          (currentId) => !previousIds.includes(currentId),
        );

        if (pendingContentsIds.length) {
          this.$store.dispatch('GET_CONTENTS_REACTIONS', pendingContentsIds);
        }
      },
    },
  },
  async created() {
    if (!this.discussion) {
      await this.$router.push({
        name: 'home',
      });
      return;
    }

    await this.pullUserProfiles();
  },
  methods: {
    async pullUserProfiles() {
      if (!this.channel) {
        await this.$store.dispatch('PULL_FORUM_API_CHANNEL_INFO', {
          channelId: this.$route.params.channelId,
        });
      }
      await this.$store.dispatch(
        'GET_PROFILES_BY_DEX_IDS',
        this.channel && this.channel.uniqueParticipantIdList,
      );
    },
    trackingCommentSubmited(isReply: boolean): void {
      Analytics.trackEvent(EVENT_LABELS.forumCommentPosted, {
        discussion_id: this.discussion.id,
        discussion_name: this.discussion.name,
        channel_id: this.channel.id,
        channel_name: this.channel.name,
        is_reply: isReply,
        category: getCourseCategory(this.course),
      });
    },
    async onReplySubmit(data: {
      comment: UnifiedCommentModel;
      text: string;
      files: FileModel[];
    }): Promise<void> {
      const { text, comment, files: attachments } = data || {};

      if (text && comment) {
        this.isSending = true;

        await this.$store
          .dispatch('CREATE_FORUM_API_COMMENT_REPLY', {
            quotedCommentId: comment.id,
            userId: this.user.id,
            text,
            attachments,
          })
          .finally(() => {
            this.trackingCommentSubmited(true);
            this.isSending = false;
          });
      }
    },
    async onCommentSubmit(params: { text: string; files: FileModel[] }): Promise<void> {
      const { text, files: attachments } = params;
      if (text) {
        this.isSending = true;

        await this.$store
          .dispatch('CREATE_FORUM_API_COMMENT', {
            discussionId: this.discussion.id,
            userId: this.user.id,
            text,
            attachments,
          })
          .finally(() => {
            this.trackingCommentSubmited(false);
            this.isSending = false;
          });
      }
    },
    async updateCommentScore(comment: UnifiedCommentModel): Promise<void> {
      if (comment) {
        this.$store.dispatch('UPDATE_FORUM_COMMENT_SCORE', comment);
      }
    },
    async deleteComment(comment: UnifiedCommentModel): Promise<void> {
      if (comment) {
        this.$store.dispatch('DELETE_FORUM_COMMENT', comment);
      }
    },
    async onCommentEdit(params: {
      text: string;
      files: FileModel[];
      commentId: number;
    }): Promise<void> {
      const { text, commentId, files: attachments } = params || {};

      if (commentId && text) {
        this.isSending = true;

        this.$store
          .dispatch('UPDATE_FORUM_COMMENT', { text, attachments, commentId })
          .finally(() => {
            this.isSending = false;
          });
      }
    },
    async deleteDiscussion() {
      this.$store.dispatch('DELETE_FORUM_DISCUSSION', this.discussion).finally(() => {
        this.$router.push({
          name: 'channel',
          params: {
            channelId: this.channel.id,
          },
        });
      });
    },
    async onDiscussionEdit(params: { text: string; files: FileModel[] }): Promise<void> {
      const { text, files: attachments } = params;

      if (text && this.discussion.id) {
        this.isSending = true;
        const discussionId = this.discussion.id;

        await this.$store
          .dispatch('UPDATE_FORUM_DISCUSSION', { text, attachments, discussionId })
          .finally(() => {
            this.isSending = false;
          });
      }
    },
    handlerScroll() {
      this.isDiscussionName = window.scrollY > this.post.$el.offsetTop;
    },
  },
});
